import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { isInRnWebView } from '../../bridge/webviewBridge';
import { APP_CONFIG } from '../../config/appConfig';
import { linkPhoneToUid, getUserByPhone, linkSocialToExistingUser, initUserDoc } from '../../services/userProfileService';
import { getLastSocialProvider } from '../../services/authService';
import { toE164KR, genOtp6, sendOtpViaProxy } from '../../services/recoveryService';

const PRIMARY = "#4A6741";

function normalizePhone(raw) {
  return raw.replace(/[^0-9]/g, "");
}

function isTestNumber(phone) {
  const n = normalizePhone(phone);
  const { start, end } = APP_CONFIG.testRange;
  return n >= start && n <= end;
}

const isIosWebView = () => {
  return isInRnWebView() && /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const LinkPhonePage = () => {
  const navigate = useNavigate();
  const { currentUser, refreshUser, logout } = useAuth();

  const [phone, setPhone] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [serverCode, setServerCode] = useState('');
  const [testCode, setTestCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [timer, setTimer] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const startTimer = () => {
    setTimer(180);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) { clearInterval(intervalRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    const normalized = normalizePhone(phone);
    if (normalized.length < 10) {
      setError('올바른 전화번호를 입력해주세요.');
      return;
    }
    setBusy(true);
    setError('');

    if (isTestNumber(normalized)) {
      const otp = genOtp6();
      setTestCode(otp);
      setServerCode(otp);
      setCodeSent(true);
      startTimer();
      setBusy(false);
      return;
    }

    const otp = genOtp6();
    setServerCode(otp);

    try {
      await sendOtpViaProxy({ phone: normalized, authcode: otp });
      setCodeSent(true);
      startTimer();
    } catch (err) {
      setError(err.message || 'SMS 발송에 실패했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    if (!code.trim()) {
      setError('인증번호를 입력해주세요.');
      return;
    }
    if (serverCode && code.trim() !== serverCode) {
      setError('인증번호가 일치하지 않습니다. 재전송 해주세요.');
      setCode('');
      setServerCode('');
      setTestCode('');
      if (intervalRef.current) clearInterval(intervalRef.current);
      setTimer(0);
      return;
    }

    setBusy(true);
    setError('');

    const phoneE164 = toE164KR(phone);

    try {
      const existingUser = await getUserByPhone(phoneE164);

      const currentProvider = getLastSocialProvider() || currentUser.providerData?.[0]?.providerId || '';

      if (existingUser && existingUser.uid !== currentUser.uid) {
        // 기존 사용자에 소셜 연결 + 소셜 빈 문서 삭제
        await linkSocialToExistingUser({
          existingUid: existingUser.uid,
          socialUid: currentUser.uid,
          provider: currentProvider,
        });
        await linkPhoneToUid({ uid: existingUser.uid, phoneE164 });
      } else {
        // 기존 사용자 없으면 문서 생성 + 전화번호 저장
        await initUserDoc({ uid: currentUser.uid, email: currentUser.email || '', provider: currentProvider });
        await linkPhoneToUid({ uid: currentUser.uid, phoneE164 });
      }
      await refreshUser();
      navigate('/', { replace: true });
    } catch (err) {
      console.error('전화번호 연동 실패:', err);
      setError('전화번호 인증에 실패했습니다. (' + (err.message || err.code || '') + ')');
    } finally {
      setBusy(false);
    }
  };

  const formatTimer = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <Container>
      <Title>전화번호 인증</Title>
      <Desc>전화번호 인증이 필요합니다.</Desc>

      <FormArea>
        <Label>전화번호</Label>
        <Input
          type="tel"
          placeholder="01012345678"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={codeSent}
        />
        <SendBtn onClick={handleSendCode} disabled={busy || (codeSent && timer > 0)}>
          {codeSent ? '재전송' : '인증번호 전송'}
        </SendBtn>

        {codeSent && (
          <>
            <Label>인증번호 {timer > 0 && <TimerText>{formatTimer(timer)}</TimerText>}</Label>
            <Input
              type="text"
              inputMode="numeric"
              placeholder="6자리 인증번호"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            {testCode && <TestCodeBox>테스트 코드: {testCode}</TestCodeBox>}
          </>
        )}

        {error && <ErrorText>{error}</ErrorText>}

        {codeSent && (
          <VerifyBtn onClick={handleVerify} disabled={busy}>
            {busy ? '인증 중...' : '인증 완료'}
          </VerifyBtn>
        )}

        {!isIosWebView() && (
          <SkipBtn onClick={() => { logout(); navigate('/login', { replace: true }); }}>
            로그인 화면으로 이동
          </SkipBtn>
        )}
      </FormArea>
    </Container>
  );
};

export default LinkPhonePage;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #EEF2EE 0%, #F4F3F8 40%);
  padding: 80px 24px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.p`
  font-size: 22px;
  text-align: center;
  margin-bottom: 8px;
  color: #1a1a1a;
`;

const Desc = styled.p`
  font-size: 15px;
  color: #888;
  text-align: center;
  margin-bottom: 40px;
`;

const FormArea = styled.div`
  width: 100%;
  max-width: 480px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 10px;
  margin-top: 20px;
  color: #333;
`;

const TimerText = styled.span`
  color: ${PRIMARY};
  font-variant-numeric: tabular-nums;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 18px;
  border: 1px solid #ddd;
  border-radius: 10px;
  font-size: 16px;
  &:focus { border-color: ${PRIMARY}; outline: none; }
  &:disabled { background: #f5f5f5; }
`;

const SendBtn = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: 12px;
  border-radius: 28px;
  background: ${PRIMARY};
  color: white;
  font-size: 16px;
  &:disabled { opacity: 0.5; }
`;

const VerifyBtn = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: 24px;
  border-radius: 28px;
  background: ${PRIMARY};
  color: white;
  font-size: 16px;
  &:disabled { opacity: 0.5; }
`;

const SkipBtn = styled.button`
  width: 100%;
  padding: 14px;
  margin-top: 12px;
  background: none;
  color: #999;
  font-size: 14px;
  text-decoration: underline;
`;

const TestCodeBox = styled.div`
  margin-top: 10px;
  padding: 12px;
  background: #FFF3E0;
  border-radius: 10px;
  font-size: 14px;
  color: #E65100;
  text-align: center;
`;

const ErrorText = styled.p`
  color: #EF5350;
  font-size: 14px;
  margin-top: 10px;
`;
